import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError, call } from "../api/client";
import { keys, refreshOpenCharacters, useBookings, useCharacterUpdate, useLog, useTransitionCheck, useUndoRedo, useUsers } from "../api/queries";
import type { CharacterView, PackageInfo } from "../api/types";
import { useCharacter } from "../character";
import { ConfirmDialog } from "../components/Dialog";
import { MessageList } from "../components/MessageList";
import { QuickActions } from "../components/QuickActions";
import { ValueRow } from "../components/Value";
import { Sheet } from "../components/Sheet";
import { systemUi } from "../registry";
import { dateTime, text } from "../text";
import { useCurrentUser } from "./AuthPages";

export function PlayPage() {
  const { character } = useCharacter();
  const Panel = systemUi(character.system)?.playPanel;
  return (
    <div className="stack">
      <Balances />
      <QuickActions />
      {Panel && <Panel />}
    </div>
  );
}

function Balances() {
  const { character, language, amount } = useCharacter();
  return (
    <div className="balances">
      {character.balances.map((b) => (
        <div key={b.currency} className="balance card">
          <span className="muted">{text(b.label, language)}</span>
          <strong>{amount(b.currency, b.balance)}</strong>
          <span className="muted small">
            {b.kind === "experience"
              ? `erhalten ${amount(b.currency, b.credited)} · ausgegeben ${amount(b.currency, b.spent)}`
              : `ein ${amount(b.currency, b.credited)} · aus ${amount(b.currency, b.spent)}`}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * The log with linear undo and redo, and per entry "Zurücknehmen": it takes back that one entry and keeps everything after it. The preview
 * shows what changes and what comes back; when a later entry builds on it, the server says which one to take back first.
 */
export function LogPage() {
  const { character, amount, confirm } = useCharacter();
  const log = useLog(character.id);
  const { undo, redo } = useUndoRedo(character.id);
  return (
    <div className="stack">
      <div className="actions">
        <button type="button" className="button secondary" disabled={!character.undoSummary} onClick={() => undo.mutate()}>
          Rückgängig{character.undoSummary ? `: ${character.undoSummary}` : ""}
        </button>
        <button type="button" className="button secondary" disabled={!character.redoSummary} onClick={() => redo.mutate()}>
          Wiederholen{character.redoSummary ? `: ${character.redoSummary}` : ""}
        </button>
      </div>
      {(character.undoRefusal || character.redoRefusal) && (
        <p className="muted small">{character.undoRefusal ?? character.redoRefusal}</p>
      )}
      <p className="muted small">„Zurücknehmen“ hebt einen einzelnen Eintrag auf, auch einen älteren, und lässt alles Spätere stehen. Ausgegebene EP und Geld kommen zurück.</p>
      <ol className="log">
        {log.data?.map((entry) => (
          <li key={entry.sequence} className={`log-entry ${entry.status}${entry.imported ? " imported" : ""}`}>
            <span className="log-time">{dateTime(entry.at)}</span>
            <span className="log-summary">
              <span className="log-text">{entry.summary}</span>
              {entry.count > 1 && <span className="badge">×{entry.count}</span>}
              {entry.status === "undone" && <span className="badge">rückgängig</span>}
              {entry.status === "reverted" && <span className="badge">zurückgenommen</span>}
              {entry.imported && <span className="badge">importiert</span>}
            </span>
            {entry.bookings.length > 0 && (
              <span className="log-bookings">
                {entry.bookings.map((b) => (
                  <span key={b.id} className={`${b.amount < 0 ? "down" : "up"}${b.active ? "" : " inactive"}`}>
                    {amount(b.currency, b.amount, true)}
                  </span>
                ))}
              </span>
            )}
            {(entry.userName || entry.originName) && (
              <span className="muted small">{[entry.userName, entry.originName && `über ${entry.originName}`].filter(Boolean).join(" · ")}</span>
            )}
            {entry.revertible && (
              <button
                type="button"
                className="link log-revert"
                onClick={() => confirm({ type: "core.revert", payload: { entry: entry.sequence } }, `Zurücknehmen: ${entry.summary}`)}
              >
                Zurücknehmen
              </button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function LedgerPage() {
  const { character, apply, language, amount: format } = useCharacter();
  const bookings = useBookings(character.id);
  const [currency, setCurrency] = useState(character.balances[0]?.currency ?? "");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const units = character.currencies.find((c) => c.id === currency)?.units ?? [];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!amount.trim()) return;
    if (await apply({ type: "core.book", payload: { currency, amount, reason } })) {
      setAmount("");
      setReason("");
    }
  };

  return (
    <div className="stack">
      <Balances />
      <form className="card booking-form" onSubmit={submit}>
        <h3>Buchen</h3>
        <label className="field">
          <span>Währung</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {character.balances.map((b) => (
              <option key={b.currency} value={b.currency}>
                {text(b.label, language)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Betrag (negativ für Ausgaben)</span>
          <input
            inputMode={units.length > 1 ? "text" : "decimal"}
            placeholder={units.length > 1 ? units.map((u, i) => `${i + 1} ${u.symbol}`).join(" ") : undefined}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>Anlass</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
        <button type="submit" className="button primary">
          Buchen
        </button>
      </form>
      <table className="ledger">
        <thead>
          <tr>
            <th>Datum</th>
            <th>Anlass</th>
            <th>Betrag</th>
          </tr>
        </thead>
        <tbody>
          {bookings.data?.map((b) => (
            <tr key={b.id} className={b.active ? "" : "inactive"}>
              <td>{dateTime(b.occurredAt)}</td>
              <td>
                {b.reason}
                {b.refund && <span className="badge">Erstattung</span>}
                {!b.active && <span className="badge">rückgängig</span>}
              </td>
              <td className={b.amount < 0 ? "down" : "up"}>{format(b.currency, b.amount, true)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SettingsPage() {
  const { character, system, apply, language } = useCharacter();
  const user = useCurrentUser();
  const users = useUsers(user.role === "admin");
  const update = useCharacterUpdate(character.id);
  const client = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(character.name);
  const [transition, setTransition] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const books = character.ruleset.books;
  const toggleBook = (code: string, on: boolean) => {
    const active = books.filter((b) => b.active).map((b) => b.code);
    void apply({ type: "core.settings", payload: { books: on ? [...active, code] : active.filter((c) => c !== code) } });
  };
  const chosen = character.settings.packages ?? [];
  const excluded = character.settings.excludedPackages ?? [];
  const selected = (p: PackageInfo) => (p.default ? !excluded.includes(p.id) : chosen.some((x) => x.id === p.id));
  const togglePackage = (p: PackageInfo, on: boolean) => {
    const packages = on ? (p.default ? chosen : [...chosen, { id: p.id }]) : chosen.filter((x) => x.id !== p.id);
    const excludedPackages = p.default ? (on ? excluded.filter((x) => x !== p.id) : [...excluded, p.id]) : excluded;
    void apply({ type: "core.settings", payload: { packages, excludedPackages } });
  };
  // An option of a package shows only while that package is loaded.
  const options = (system?.options ?? []).filter((o) => !o.requiresPackage || character.ruleset.packages.some((x) => x.id === o.requiresPackage));
  const setOption = (key: string, value: unknown) => {
    const options = character.settings.options ?? {};
    void apply({ type: "core.settings", payload: { options: { ...options, values: { ...(options.values ?? {}), [key]: value } } } });
  };

  return (
    <div className="stack">
      <section className="card">
        <h3>Name</h3>
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void apply({ type: "core.rename", payload: { name } });
          }}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} />
          <button type="submit" className="button secondary">
            Umbenennen
          </button>
        </form>
      </section>

      {character.mode === "creation" && (
        <section className="card" id="karriere">
          <h3>Karrieremodus</h3>
          <p className="muted">Nach dem Wechsel gelten die Steigerungsregeln statt der Erschaffungsregeln. Der Wechsel lässt sich im Protokoll rückgängig machen.</p>
          <button type="button" className="button primary" onClick={() => setTransition(true)}>
            In den Karrieremodus wechseln …
          </button>
        </section>
      )}

      <section className="card">
        <h3>Datenpakete und Quellen</h3>
        {system?.packages.map((p) => {
          const active = character.ruleset.packages.some((x) => x.id === p.id);
          return (
            <label key={p.id} className="option">
              <input type="checkbox" checked={selected(p)} onChange={(e) => togglePackage(p, e.target.checked)} />
              <span>
                {text(p.title, language)} <span className="muted">{p.version}</span>
                {p.default && <span className="badge">Standard</span>}
                {p.origin === "local" && <span className="badge">hochgeladen</span>}
                {p.requires.length > 0 && <span className="muted small"> braucht {p.requires.map((r) => text(system.packages.find((x) => x.id === r)?.title, language) || r).join(", ")}</span>}
                {selected(p) && !active && <span className="muted small"> (nicht geladen, siehe Meldung)</span>}
              </span>
            </label>
          );
        })}
        {chosen
          .filter((c) => !system?.packages.some((p) => p.id === c.id))
          .map((c) => (
            <label key={c.id} className="option">
              <input type="checkbox" checked onChange={() => void apply({ type: "core.settings", payload: { packages: chosen.filter((x) => x.id !== c.id) } })} />
              <span>
                {c.id} <span className="muted small">nicht mehr installiert; abwählen entfernt die Wahl</span>
              </span>
            </label>
          ))}
        {books.length > 1 &&
          books.map((b) => (
            <label key={b.code} className="option">
              <input type="checkbox" disabled={b.required} checked={b.active} onChange={(e) => toggleBook(b.code, e.target.checked)} />
              <span>
                {text(b.title, language)} <span className="muted">{b.code}</span>
              </span>
            </label>
          ))}
        <MessageList messages={character.ruleset.messages.map((m, i) => ({ message: m, key: `${m.code}-${i}`, acknowledged: false }))} compact />
      </section>

      {options.length > 0 && (
        <section className="card">
          <h3>Regeloptionen</h3>
          {options.map((o) => {
            const values = (character.settings.options?.values ?? {}) as Record<string, unknown>;
            const current = values[o.key] ?? o.default;
            return (
              <label key={o.key} className="option">
                {o.type === "bool" ? (
                  <input type="checkbox" checked={current === true} onChange={(e) => setOption(o.key, e.target.checked)} />
                ) : o.type === "choice" ? (
                  <select value={String(current ?? "")} onChange={(e) => setOption(o.key, e.target.value)}>
                    {(o.choices ?? []).map((c) => (
                      <option key={c.value} value={c.value}>
                        {text(c.label, language)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input defaultValue={String(current ?? "")} onBlur={(e) => setOption(o.key, o.type === "int" || o.type === "decimal" ? Number(e.target.value) : e.target.value)} />
                )}
                <span>
                  {text(o.label, language)}
                  {o.description && <span className="muted"> {text(o.description, language)}</span>}
                </span>
              </label>
            );
          })}
        </section>
      )}

      <section className="card">
        <h3>Ausgabe</h3>
        <div className="actions">
          {system?.hasSheet && (
            <a className="button secondary" href={`/api/characters/${character.id}/sheet.pdf`} target="_blank" rel="noreferrer">
              Charakterbogen (PDF)
            </a>
          )}
          {system?.formats
            .filter((f) => f.canExport)
            .map((f) => (
              <a key={f.id} className="button secondary" href={`/api/characters/${character.id}/export/${f.id}`} download>
                {text(f.label, language)}
              </a>
            ))}
        </div>
      </section>

      {user.role === "admin" && users.data && (
        <section className="card">
          <h3>Spieler zuweisen</h3>
          <select
            value={character.ownerId}
            onChange={async (e) => {
              setError(null);
              try {
                update(await call<CharacterView>(api.POST("/api/characters/{id}/owner", { params: { path: { id: character.id } }, body: { userId: e.target.value } })));
              } catch (err) {
                setError(err instanceof ApiError ? err.message : String(err));
              }
            }}
          >
            {users.data.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </section>
      )}

      <section className="card danger">
        <h3>Löschen</h3>
        <button type="button" className="button danger" onClick={() => setDeleting(true)}>
          Charakter löschen
        </button>
      </section>
      {error && <p className="error">{error}</p>}
      {transition && <TransitionDialog onClose={() => setTransition(false)} />}
      {deleting && (
        <ConfirmDialog
          title="Charakter löschen"
          action="Endgültig löschen"
          danger
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await call(api.DELETE("/api/characters/{id}", { params: { path: { id: character.id } } }));
            await client.invalidateQueries({ queryKey: keys.characters });
            refreshOpenCharacters(client);
            navigate("/");
          }}
        >
          <p>„{character.name}“ wird mit allen Änderungen und Buchungen gelöscht. Das lässt sich nicht rückgängig machen.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function TransitionDialog(props: { onClose: () => void }) {
  const { character, apply, language, amount } = useCharacter();
  const check = useTransitionCheck(character.id, true);
  const [confirmed, setConfirmed] = useState(false);
  const navigate = useNavigate();
  const open = check.data?.openPoints.filter((m) => !m.acknowledged) ?? [];
  return (
    <Sheet
      title="In den Karrieremodus wechseln"
      onClose={props.onClose}
      footer={
        <>
          <button type="button" className="button secondary" onClick={props.onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="button primary"
            disabled={!confirmed}
            onClick={async () => {
              if (await apply({ type: "core.transition", payload: { confirm: true } })) {
                props.onClose();
                navigate(`/c/${character.id}/spiel`);
              }
            }}
          >
            Wechseln
          </button>
        </>
      }
    >
      {check.isLoading && <p className="muted">Prüfe offene Punkte …</p>}
      {check.data?.summary && <p>{text(check.data.summary, language)}</p>}
      {open.length > 0 ? (
        <>
          <p>Diese Punkte sind noch offen:</p>
          <MessageList messages={open} compact />
        </>
      ) : (
        check.data && <p>Es sind keine Punkte mehr offen.</p>
      )}
      {(check.data?.openingBookings.length ?? 0) > 0 && (
        <>
          <p>Beim Wechsel wird gebucht:</p>
          <ul>
            {check.data!.openingBookings.map((b, i) => (
              <li key={i}>
                {amount(b.currency, b.amount, true)}: {b.reason}
              </li>
            ))}
          </ul>
        </>
      )}
      <label className="option">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
        <span>Ich habe die offenen Punkte gesehen und möchte wechseln.</span>
      </label>
    </Sheet>
  );
}

/** Every computed value with its derivation; useful for checking and for systems without their own sheet yet. */
export function ValuesPage() {
  const { character, language } = useCharacter();
  const [filter, setFilter] = useState("");
  const entries = Object.values(character.evaluation.values)
    .filter((v) => !filter || v.key.includes(filter) || text(v.label, language).toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => a.key.localeCompare(b.key));
  return (
    <div className="stack">
      <input type="search" placeholder="Wert suchen …" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Wert suchen" />
      <p className="muted small">{entries.length} Werte. Antippen zeigt die Herleitung.</p>
      <div className="card">
        {entries.map((v) => (
          <ValueRow key={v.key} valueKey={v.key} hint={v.key} />
        ))}
      </div>
    </div>
  );
}
